import { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeftRight,
  ChartLine,
  Film,
  Gift,
  PackageSearch,
  ShieldAlert,
  Users,
} from "lucide-react";

import { useAuthenticatedUser, useAuthentication } from "@lib/authentication";
import {
  TWO_FACTOR_PATH,
  useTotpEnabled,
} from "@lib/authentication/two-factor";
import { MenuBar } from "@pages/v2/components/layout/MenuBar";
import { Sidebar } from "@pages/v2/components/layout/Sidebar";

import "./AdminPortal.module.css";

const adminRoutes = [
  {
    path: "users",
    title: "Users",
    icon: <Users />,
  },
  {
    path: "gifts",
    title: "Gifts",
    icon: <Gift />,
  },
  {
    path: "product-catalog",
    title: "Product Catalog",
    icon: <PackageSearch />,
  },
  {
    path: "transactions",
    title: "Transactions",
    icon: <ArrowLeftRight />,
  },
  {
    path: "videos",
    title: "Videos",
    icon: <Film />,
  },
  {
    path: "checkout",
    title: "Checkout",
    icon: <ChartLine />,
  },
  {
    path: "health",
    title: "Health",
    icon: <Activity />,
  },
];

const AdminPortal = () => {
  const { authenticatedUser, isLoading } = useAuthenticatedUser();
  const { data: totpEnabled } = useTotpEnabled();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthentication();

  useEffect(() => {
    if (!isLoading && !authenticatedUser?.groups.includes("admins")) {
      console.log("not admin, signing out");
      signOut().then(() => {
        localStorage.removeItem("si");
        localStorage.removeItem("ck");
        navigate("/login");
      });
    }
  }, [isLoading, authenticatedUser, navigate, signOut]);

  if (isLoading || !authenticatedUser?.groups.includes("admins")) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-100">
      <div className="flex-none">
        <MenuBar user={authenticatedUser as { email: string }} />
      </div>
      <div className="flex grow overflow-hidden">
        <div className="flex-none h-full">
          <Sidebar navItems={adminRoutes} />
        </div>
        <main className="grow p-4 overflow-scroll">
          {totpEnabled === false && pathname !== TWO_FACTOR_PATH && (
            <div
              role="status"
              className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3"
            >
              <ShieldAlert className="flex-none text-amber-700" />
              <span>
                Admin accounts need two-factor sign-in. Soon the admin pages
                will not load without it.
              </span>
              <Link
                className="ml-auto font-semibold text-cta"
                to={TWO_FACTOR_PATH}
              >
                Set it up
              </Link>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminPortal;
